import clsx from "clsx";
import { select } from "gamedata/GameData";
import { useLoaderData } from "react-router";
import { ProfLink } from "~/components/prof/ProfLink";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { PageTitle } from "~/components/ui/PageTitle";
import { gameName } from "~/util/common";
import { tt, useT, useTurl } from "~/util/Lang";
import { MetaFunc, OurLoaderArgs } from "~/util/Loaders";
import { homePage } from "~/util/titles";

import css from "./classes.module.css";
import { logPageView } from "~/util/stats";

type Prof = {
  profName: string;
  name: string;
  prev?: Prof | null;
  children?: Prof[];
};

type Race = {
  raceName: string;
  children: Prof[];
};

async function makeData(la: OurLoaderArgs) {
  // Log
  logPageView(la);
  //
  const t = tt.bind(null, la.realm.lang);

  return {
    profs: select(Array.from(la.gd.profs.values()), {
      select: {
        profName: true,
        name: t,
        prev: { profName: true, name: t },
      },
    }),
  };
}

type LoaderData = Awaited<ReturnType<typeof makeData>>;

export const loader = async (_la: OurLoaderArgs) => {
  const data = await makeData(_la);
  return { ...data, authUser: _la.req.sess?.user };
};

export const meta: MetaFunc<LoaderData> = (cx) => {
  return {
    title: `${cx.t({ en: `Classes`, ru: `Классы` })} - ${homePage(cx.realm)}`,
    description: cx.t({
      en: `A complete searchable and filterable list of all Classes in ${gameName(
        cx.realm.game
      )}.`,
      ru: `Полный список всех классов ${gameName(
        cx.realm.game
      )}. Скилы по уровням, необходимые требования и предметы для изучения.`,
    }),
    ogTitle: cx.t({
      en: `Classes ${gameName(cx.realm.game)}`,
      ru: `Классы ${gameName(cx.realm.game)}`,
    }),
  };
};

export default function Profs() {
  const data = useLoaderData() as LoaderData;
  const t = useT();
  const turl = useTurl();
  const classesTitle = t({ en: "Classes", ru: "Классы" });

  return (
    <div>
      <div className="content">
        <Breadcrumb items={[{ title: classesTitle, url: turl("/classes") }]} />
        <PageTitle>{classesTitle}</PageTitle>
      </div>

      <div className={css.classList}>
        {buildHierarchy(data.profs).map((r, i) => (
          <div key={i}>
            <div className={css.race}>{r.raceName}</div>
            <div className="columns" style={{ gap: 2 }}>
              {generate(r.children)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function buildHierarchy(profs: Prof[]) {
  const raceMap = new Map<string, Race>();
  const races = new Map([
    ["fighter", "human"],
    ["mage", "human"],
    ["elven_fighter", "elf"],
    ["elven_mage", "elf"],
    ["dark_fighter", "darkelf"],
    ["dark_mage", "darkelf"],
    ["orc_fighter", "orc"],
    ["orc_mage", "orc"],
    ["dwarven_fighter", "dwarf"],
    ["kamael_m_soldier", "kamael"],
    ["kamael_f_soldier", "kamael"],
  ]);
  let profMap = new Map();

  // Initialize the map with profession objects
  profs.forEach((prof) => {
    prof.children = []; // Initialize children array
    profMap.set(prof.profName, prof);
  });

  // Add children to their respective parent
  profs.forEach((prof) => {
    if (prof.prev && prof.prev.profName) {
      let parent = profMap.get(prof.prev.profName);
      if (parent) {
        parent.children.push(prof);
      }
    }
  });

  // Filter out top-level professions (those without a parent)
  const newProfs = profs.filter((prof) => !prof.prev);

  for (const prof of newProfs) {
    const raceName = races.get(prof.profName);
    if (raceName) {
      const race = raceMap.get(raceName);
      if (!race) {
        raceMap.set(raceName, { raceName, children: [prof] });
      } else {
        race.children.push(prof);
      }
    }
  }

  return Array.from(raceMap.values());
}

function generate(profs: Prof[], r = 0) {
  return profs.map((p, i) => {
    return (
      <div
        key={i}
        className={clsx({
          column: !r,
          [css.r0]: !r,
          [css.r1]: r === 1,
          [css.r2]: r === 2,
          [css.r3]: r === 3,
        })}
      >
        <div
          style={{
            // paddingLeft: r * 12,
            padding: "3px 0px 3px 0px",
          }}
        >
          <ProfLink data={p} iconSize={16} />
        </div>
        {p.children && generate(p.children, r + 1)}
      </div>
    );
  });
}
